import { searchFilterPropertyFiltersToFormValues } from "../searchFilterSlots.js";
import { useApolloClient } from "@apollo/client";
import { ModalOpenButton } from "@msys/ui";
import { Button, ButtonProps, SvgIcon } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { TplApplicableFor } from "../../../../clients/graphqlTypes.js";
import { templateSearchSortingDefaultsToSearchSortingInputs } from "../../templates/helpers.js";
import {
  CreateItemsFromTemplatesArgs,
  CreateItemsWithProductsArgs,
} from "../hooks/useCreateItem.js";
import type { TreeItemTemplatePlaceholderFragment } from "../Fragments.generated.js";
import { SearchItemInputModal } from "../SearchItemInputModal.js";
import {
  FormValues,
  isCartSingleTemplateOnly,
} from "../SearchProductsAndTemplates.js";
import { TemplateFiltersFormValues } from "../../templates/filters/TemplateFilterFields.js";
import { useSetItemTemplatePlaceholderTemplateMutation } from "./TemplatePlaceholderSelectTemplateButton.generated.js";

/**
 * The "Fill placeholder" CTA — the only entry point into the multi-select
 * picker (design C1). The tree/data-grid "find and replace" icon is a
 * separate, pre-existing single-template swap and is untouched.
 *
 * Only rendered by `TemplatePlaceholderEmptyStateBox` (already empty-only),
 * so there's no filled/hand-built state to handle here.
 */
interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  templateTypeName: string;
  /** The placeholder's own template type. The backend asserts a linked
   *  template implements exactly this type, so it decides which insert path
   *  a single-template cart may take — see `handleSelect`. */
  templateTypeId: string;
  templateSearchFilterDefaults: Omit<
    TreeItemTemplatePlaceholderFragment["templateSearchFilterDefaults"],
    "__typename"
  >;
  templateSearchSortingDefaults: Omit<
    TreeItemTemplatePlaceholderFragment["templateSearchSortingDefaults"],
    "__typename"
  >;
  templateApplicableFor: TplApplicableFor;
  createItemsWithProducts: (
    args: CreateItemsWithProductsArgs
  ) => Promise<unknown>;
  createItemsFromTemplates: (
    args: CreateItemsFromTemplatesArgs
  ) => Promise<unknown>;
  onCompleted?: (itemId: string) => void;
  color?: ButtonProps["color"];
  variant?: ButtonProps["variant"];
  label: string;
  /** Pass null to render the button without a leading icon. */
  Icon?: typeof SvgIcon | null;
  refetchWizardQuestions: () => Promise<unknown>;
}

export const TemplatePlaceholderFillButton = ({
  projectId,
  docId,
  itemId,
  templateTypeName,
  templateTypeId,
  templateSearchFilterDefaults,
  templateSearchSortingDefaults,
  templateApplicableFor,
  createItemsWithProducts,
  createItemsFromTemplates,
  onCompleted,
  color = "primary",
  variant = "contained",
  label,
  Icon,
  refetchWizardQuestions,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "TemplatesSearch", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [setItemTemplatePlaceholderTemplateMutation] =
    useSetItemTemplatePlaceholderTemplateMutation({
      client,
      onCompleted: ({
        setItemTemplatePlaceholderTemplate: {
          item: { id },
        },
      }) => onCompleted?.(id),
    });

  // Refreshing the wizard is a follow-up to work that already committed, so it
  // must never decide `handleSelect`'s outcome: a rejection there keeps the
  // picker open with a cart whose items are already in the tree, which is the
  // duplicate insert both branches below go out of their way to prevent.
  const refreshWizardQuestions = async () => {
    try {
      await refetchWizardQuestions();
    } catch {
      // Stale questions until the next refresh — strictly better than a
      // resubmittable cart.
    }
  };

  const button = (
    <Button
      color={color}
      variant={variant}
      startIcon={Icon ? <Icon /> : undefined}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {label}
    </Button>
  );

  return (
    <ModalOpenButton
      Modal={SearchItemInputModal}
      modalProps={{
        initialSearchValue: "",
        initialSearchTab: "templates" as const,
        applicableFor: [templateApplicableFor],
        title: t("Configuring placeholder", { ns: "TemplatesSearch" }),
        destination: {
          itemId,
          title: templateTypeName,
        },
        initialTemplateFilters: filterDefinitionsToFormValues(
          templateSearchFilterDefaults
        ),
        seedPropertySortings:
          templateSearchSortingDefaultsToSearchSortingInputs(
            templateSearchSortingDefaults
          ),
        handleSelect: async ({ products, templates }: FormValues) => {
          // D1: a single template at qty 1 preserves the template link;
          // everything else nests as hand-built content. The two "everything
          // else" mutations are separate calls, sequenced products-then-
          // templates with rank: null, so they append in cart order onto the
          // always-empty placeholder.
          //
          // The type check is load-bearing, not defensive: D3 makes the
          // picker's template type an editable search scope, so the chosen
          // template need not implement the placeholder's own type. The
          // linked path asserts server-side that it does
          // (`item.ts`'s `aggregate_createItemFromTemplate`), so a template
          // found after re-scoping has to nest as hand-built content instead
          // of failing the mutation.
          if (
            isCartSingleTemplateOnly({ products, templates }) &&
            templates[0].template.resolvedAsReadModelVersionNumber &&
            templates[0].template.implementsTemplateType?.templateType.id ===
              templateTypeId
          ) {
            await setItemTemplatePlaceholderTemplateMutation({
              variables: {
                input: {
                  docId,
                  itemId,
                  projectId,
                  templateQuoteId: templates[0].template.id,
                  templateQuoteIdAtVersionNumber:
                    templates[0].template.resolvedAsReadModelVersionNumber,
                },
              },
            });
          } else {
            // Products and templates are two mutations, so a mixed cart can
            // commit the first and fail the second. The picker keeps itself
            // open when `handleSelect` throws, and its cart still holds the
            // products that already landed — retrying would insert them twice.
            // Once anything has committed, the error is reported and the
            // picker allowed to close, so retrying means re-opening it against
            // a placeholder whose partial contents are already visible in the
            // tree. Before that point nothing is committed, so the throw
            // propagates and the untouched cart stays retryable in place.
            let committedAnything = false;
            try {
              if (products.length > 0) {
                await createItemsWithProducts({
                  parentItemId: itemId,
                  products,
                  newItemRank: null,
                });
                committedAnything = true;
              }
              if (templates.length > 0) {
                await createItemsFromTemplates({
                  parentItemId: itemId,
                  templates,
                  newItemRank: null,
                });
              }
            } catch (e) {
              if (!committedAnything) throw e;
              // What landed is real, so the tree and the wizard still have to
              // catch up before the picker goes away.
              onCompleted?.(itemId);
              await refreshWizardQuestions();
              enqueueSnackbar(
                t("Something went wrong. Please try again.", { ns: "Global" }),
                { variant: "error" }
              );
              return;
            }
            onCompleted?.(itemId);
          }
          await refreshWizardQuestions();
          enqueueSnackbar(t("Placeholder filled", { ns: "QuoteItem" }), {
            variant: "success",
          });
        },
      }}
    >
      {button}
    </ModalOpenButton>
  );
};

function filterDefinitionsToFormValues(
  definitions: Omit<
    TreeItemTemplatePlaceholderFragment["templateSearchFilterDefaults"],
    "__typename"
  >
): TemplateFiltersFormValues {
  return {
    templateTypeIds: definitions.templateTypeIdFilter
      ? [definitions.templateTypeIdFilter.valueId]
      : null,
    ...searchFilterPropertyFiltersToFormValues(definitions.propertyFilters),
  };
}
